import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  ButtonText,
  DateInput,
  Dropzone,
  Flex,
  FormField,
  Heading,
  MultiSelect,
  Select,
  Text,
  TextArea,
  TextInput
} from '@percent/lemonade'
import type { CreateFormFormValues } from './CreateFormModal.types'
import styles from './FormPreview.module.scss'

const SCORE_DEFAULT_MAX = 5

const QUESTION_TYPE_PLACEHOLDERS: Record<string, string> = {
  text: 'Placeholder text',
  textarea: 'Placeholder text',
  number: '0',
  email: 'email@example.com',
  name: 'First Last',
  website: 'https://example.com',
  date: 'DD/MM/YYYY',
  award_amount: '0.00',
  final_award_amount: '0.00'
}

type FormPreviewProps = {
  values: CreateFormFormValues
}

export function FormPreview({ values }: Readonly<FormPreviewProps>) {
  const { t } = useTranslation()

  const hasContent =
    values.internalName || values.description || values.pages.some(page => page.questions.length > 0 || page.name)

  if (!hasContent) {
    return (
      <Flex direction="column" align="center" justify="center" className={styles.emptyState}>
        <Text size="small" color="grey500">
          {t('grants.forms.createModal.preview.empty')}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="lg" className={styles.container}>
      <Flex justify="space-between" align="center">
        <Heading level="h5">{values.internalName || t('grants.forms.createModal.preview.title')}</Heading>
        <Badge variant="informative">{t('grants.forms.createModal.preview.badge')}</Badge>
      </Flex>

      {values.description && (
        <span style={{ whiteSpace: 'pre-wrap' }}>
          <Text size="small" color="grey500">
            {values.description}
          </Text>
        </span>
      )}

      {values.pages.map((page, pageIndex) => (
        <Flex key={page.uid} direction="column" gap="md">
          {(values.pages.length > 1 || page.name) && (
            <Heading level="h6">
              {page.name || t('grants.forms.createModal.section.defaultTitle', { number: pageIndex + 1 })}
            </Heading>
          )}

          {page.description && (
            <span style={{ whiteSpace: 'pre-wrap' }}>
              <Text size="small" color="grey500">
                {page.description}
              </Text>
            </span>
          )}

          {page.questions.map(question => (
            <PreviewQuestion
              key={question.uid}
              label={question.label}
              type={question.type}
              required={question.required}
              maxScore={question.maxScore}
              options={question.options}
              multiple={question.multiple}
            />
          ))}
        </Flex>
      ))}
    </Flex>
  )
}

function PreviewQuestion({
  label,
  type,
  required,
  maxScore = SCORE_DEFAULT_MAX,
  options = [],
  multiple = false
}: {
  label: string
  type: string
  required: boolean
  maxScore?: number
  options?: { value: string; label: string }[]
  multiple?: boolean
}) {
  const { t } = useTranslation()
  const [scoreValue, setScoreValue] = useState<number | null>(null)
  const [decisionValue, setDecisionValue] = useState<string | null>(null)

  const renderInput = () => {
    switch (type) {
      case 'score':
        return (
          <Flex direction="column" gap="xs" w="100%">
            <Flex gap="xs" justify="space-between">
              {Array.from({ length: maxScore }, (_, i) => (
                <ButtonText
                  key={i + 1}
                  onClick={() => setScoreValue(i + 1)}
                  className={scoreValue === i + 1 ? styles.scoreButtonActive : styles.scoreButton}
                >
                  {i + 1}
                </ButtonText>
              ))}
            </Flex>
            <Flex justify="space-between">
              <Text size="small" color="grey500">
                {t('grants.forms.createModal.preview.score.low')}
              </Text>
              <Text size="small" color="grey500">
                {t('grants.forms.createModal.preview.score.high')}
              </Text>
            </Flex>
          </Flex>
        )
      case 'decision':
        return (
          <Flex gap="xs" w="100%">
            <ButtonText
              onClick={() => setDecisionValue('no')}
              className={`${styles.decisionButton} ${styles.fail} ${decisionValue === 'no' ? styles.failActive : ''}`}
            >
              {t('grants.forms.createModal.preview.decision.no')}
            </ButtonText>
            <ButtonText
              onClick={() => setDecisionValue('yes')}
              className={`${styles.decisionButton} ${styles.pass} ${decisionValue === 'yes' ? styles.passActive : ''}`}
            >
              {t('grants.forms.createModal.preview.decision.yes')}
            </ButtonText>
          </Flex>
        )
      case 'textarea':
        return <TextArea name={`preview-${label}`} placeholder={QUESTION_TYPE_PLACEHOLDERS[type]} />
      case 'number':
        return <TextInput name={`preview-${label}`} type="number" placeholder={QUESTION_TYPE_PLACEHOLDERS[type]} />
      case 'date':
        return (
          <DateInput
            name={`preview-${label}`}
            placeholder={QUESTION_TYPE_PLACEHOLDERS[type]}
            onChange={() => {}}
            disabled
          />
        )
      case 'documents':
        return (
          <Dropzone
            title={t('grants.forms.createModal.preview.documents.title')}
            description={t('grants.forms.createModal.preview.documents.description')}
            files={[]}
            onChange={() => {}}
            disabled
          />
        )
      case 'select':
        if (multiple) {
          return (
            <MultiSelect
              name={`preview-${label}`}
              placeholder={t('grants.forms.createModal.preview.select.placeholder')}
              options={options}
            />
          )
        }

        return (
          <Select
            name={`preview-${label}`}
            placeholder={t('grants.forms.createModal.preview.select.placeholder')}
            options={options}
          />
        )
      case 'award_recommendation':
      case 'radio':
      case 'checkbox':
        return (
          <Select
            name={`preview-${label}`}
            placeholder={t('grants.forms.createModal.preview.select.placeholder')}
            options={options}
          />
        )
      default:
        return (
          <TextInput
            name={`preview-${label}`}
            placeholder={QUESTION_TYPE_PLACEHOLDERS[type] ?? t('grants.forms.createModal.preview.placeholder.default')}
          />
        )
    }
  }

  return (
    <FormField label={label} necessity={required ? 'required' : 'optional'}>
      <Flex w="100%">{renderInput()}</Flex>
    </FormField>
  )
}
